import { useCallback } from 'react';
import { supabase, Database } from './supabase';
import { Object } from '../pages/art/utils/useArtHistory';
import { uploadImageToStorage, getPublicUrl } from './storageHelpers';
import { v4 as uuid } from 'uuid';

// Constants for storage
const STORAGE_BUCKET = 'playmix-assets';
const IMAGE_FOLDER = 'art-images';

export type Art = Database['public']['Tables']['art']['Row'] & {
  user_tag?: string;
  aiImage?: string;
  userPng?: string;
};

export type ArtHistoryEntry = Database['public']['Tables']['art_history_entries']['Row'] & {
  objects: Object[];
};

export type ArtWithHistory = Art & {
  art_history_entries: ArtHistoryEntry[];
};

// Helper functions to handle objects conversion
const parseObjectsFromDB = (objectsString: string[]): Object[] => {
  try {
    return objectsString.map(objStr => JSON.parse(objStr) as Object);
  } catch (e) {
    console.error('Error parsing objects from DB:', e);
    throw new Error('Invalid objects format in database');
  }
};

const stringifyObjectsForDB = (objects: Object[]): string[] => {
  return objects.map(obj => JSON.stringify(obj));
};

export const useArt = () => {
  const createArt = useCallback(async (artName: string): Promise<Art> => {
    // console.log('Creating new art with name:', artName);
    
    const { data: art, error: artError } = await supabase
      .from('art')
      .insert({
        art_name: artName,
      })
      .select('*')
      .single();

    if (artError) {
      console.error('Error creating art:', artError);
      throw artError;
    }
    if (!art) {
      console.error('No art returned after creation');
      throw new Error('No art returned after creation');
    }
    
    // console.log('Successfully created art:', art);
    return art;
  }, []);

  const updateArt = useCallback(async (artId: string, updates: Partial<Art>): Promise<Art> => {
    const { data: art, error: artError } = await supabase
      .from('art')
      .update(updates)
      .eq('id', artId)
      .select('*')
      .single();

    if (artError) throw artError;
    if (!art) throw new Error('No art returned after update');
    return art;
  }, []);

  const createHistoryEntry = useCallback(async (
    artId: string,
    description: string,
    userPng: string,
    aiImage: string,
    objects: Object[]
  ): Promise<ArtHistoryEntry> => {
    // Generate a unique identifier for the file names
    const uniqueId = uuid();
    
    // Upload userPng to storage if it's a base64 image
    const userPngPath = userPng ? await uploadImageToStorage(
      supabase,
      userPng,
      `art-${artId}-history-${uniqueId}-user-png`,
      IMAGE_FOLDER
    ) : '';
    
    // Upload aiImage to storage if it's a base64 image
    const aiImagePath = aiImage ? await uploadImageToStorage(
      supabase,
      aiImage,
      `art-${artId}-history-${uniqueId}-ai-image`,
      IMAGE_FOLDER
    ) : '';
    
    const { data: entry, error: entryError } = await supabase
      .from('art_history_entries')
      .insert({
        art_id: artId,
        description,
        user_png: userPngPath,
        ai_image: aiImagePath,
        objects: stringifyObjectsForDB(objects),
      })
      .select('*')
      .single();

    if (entryError) throw entryError;
    if (!entry) throw new Error('No entry returned after creation');
    
    return {
      ...entry,
      objects: parseObjectsFromDB(entry.objects)
    };
  }, []);

  const updateHistoryEntry = useCallback(async (
    iterationId: string,
    updates: Partial<Omit<ArtHistoryEntry, 'objects'>> & { objects?: Object[] }
  ): Promise<ArtHistoryEntry> => {
    // Get the current entry to determine the art ID
    const { data: currentEntry, error: getError } = await supabase
      .from('art_history_entries')
      .select('*')
      .eq('iteration_id', iterationId)
      .single();
      
    if (getError) {
      console.error('Error retrieving current art history entry:', getError);
      throw getError;
    }
    
    const artId = currentEntry.art_id;
    const updateData: any = { ...updates };
    
    // Handle objects conversion if present
    if (updates.objects) {
      updateData.objects = stringifyObjectsForDB(updates.objects);
    }

    // Generate a unique identifier for the file names
    const uniqueId = uuid();
    
    // Handle userPng upload if provided and it's a base64 image
    if (updates.user_png && updates.user_png.startsWith('data:')) {
      const userPngPath = await uploadImageToStorage(
        supabase,
        updates.user_png,
        `art-${artId}-history-${uniqueId}-user-png`,
        IMAGE_FOLDER
      );
      updateData.user_png = userPngPath;
    }
    
    // Handle aiImage upload if provided and it's a base64 image
    if (updates.ai_image && updates.ai_image.startsWith('data:')) {
      const aiImagePath = await uploadImageToStorage(
        supabase,
        updates.ai_image,
        `art-${artId}-history-${uniqueId}-ai-image`,
        IMAGE_FOLDER
      );
      updateData.ai_image = aiImagePath;
    }
    
    const { data: entry, error: entryError } = await supabase
      .from('art_history_entries')
      .update(updateData)
      .eq('iteration_id', iterationId)
      .select('*')
      .single();

    if (entryError) throw entryError;
    if (!entry) throw new Error('No entry returned after update');
    
    return {
      ...entry,
      objects: parseObjectsFromDB(entry.objects)
    };
  }, []);

  const getArt = useCallback(async (artId: string): Promise<ArtWithHistory | null> => {
    const { data: art, error: artError } = await supabase
      .from('art')
      .select(`
        *,
        art_history_entries (*, created_at),
        profiles!art_user_id_fkey (user_tag)
      `)
      .eq('id', artId)
      .single();

    if (artError) throw artError;
    if (!art) return null;
    
    // Extract user_tag from profiles and add it to root level
    art.user_tag = art.profiles?.user_tag ? '@' + art.profiles.user_tag : undefined;
    delete art.profiles;

    // Parse objects when retrieving from the database
    if (art.art_history_entries) {
      art.art_history_entries = art.art_history_entries.map(entry => {
        // Create a new object to avoid mutating the original
        const entryWithPublicUrls = {
          ...entry,
          objects: parseObjectsFromDB(entry.objects)
        };
        
        // Generate public URL for user_png if it exists and is a storage path
        if (entry.user_png && !entry.user_png.startsWith('data:')) {
          entryWithPublicUrls.user_png = getPublicUrl(supabase, entry.user_png);
        }
        
        // Generate public URL for ai_image if it exists and is a storage path
        if (entry.ai_image && !entry.ai_image.startsWith('data:')) {
          entryWithPublicUrls.ai_image = getPublicUrl(supabase, entry.ai_image);
        }
        
        return entryWithPublicUrls;
      });
    }
    
    // Sort history entries by updated_at
    if (art.art_history_entries) {
      art.art_history_entries.sort((a, b) => 
        new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
      );
    }
    
    return art as ArtWithHistory;
  }, []);

  const getArtHistory = useCallback(async (): Promise<ArtWithHistory[]> => {
    const { data: arts, error: artsError } = await supabase
      .from('art')
      .select(`
        *,
        art_history_entries (*, created_at),
        profiles!art_user_id_fkey (user_tag)
      `)
      .order('updated_at', { ascending: false });

    if (artsError) throw artsError;
    if (!arts) return [];

    // Extract user_tag and parse objects for each art
    arts.forEach(art => {
      // Extract user_tag from profiles and add it to root level
      art.user_tag = art.profiles?.user_tag ? '@' + art.profiles.user_tag : undefined;
      delete art.profiles;

      if (art.art_history_entries) {
        art.art_history_entries = art.art_history_entries.map(entry => {
          // Create a new object to avoid mutating the original
          const entryWithPublicUrls = {
            ...entry,
            objects: parseObjectsFromDB(entry.objects)
          };
          
          // Generate public URL for user_png if it exists and is a storage path
          if (entry.user_png && !entry.user_png.startsWith('data:')) {
            entryWithPublicUrls.user_png = getPublicUrl(supabase, entry.user_png);
  
          }
          
          // Generate public URL for ai_image if it exists and is a storage path
          if (entry.ai_image && !entry.ai_image.startsWith('data:')) {
            entryWithPublicUrls.ai_image = getPublicUrl(supabase, entry.ai_image);
  
          }
          
          return entryWithPublicUrls;
        });
      }
    });

    // Sort history entries by updated_at
    arts.forEach(art => {
      if (art.art_history_entries) {
        art.art_history_entries.sort((a, b) => 
          new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
        );
      }
    });

    return arts as ArtWithHistory[];
  }, []);

  return {
    createArt,
    updateArt,
    createHistoryEntry,
    updateHistoryEntry,
    getArt,
    getArtHistory,
  };
};
